import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi } from "../api/admin.api.js";

/**
 * Generic CRUD hook factory. Per Tech Impl § 8.4. Drops a thin layer over
 * TanStack Query that knows how to talk to /api/v1/admin/{resource}.
 *
 *   const { list, create, update, remove } = useResource("designs");
 *
 *   const { data, isLoading } = list({ collection: "plato-collection" });
 *   create.mutate(payload);
 *   update.mutate({ id, data: partial });
 *   remove.mutate(id);
 *
 * The mutations invalidate every query keyed off [resource] so list views
 * re-fetch automatically after a write. Toasts are wired by default with the
 * resource label; pass `{ toastLabel: "Design" }` to override.
 */
export function useResource(resource, { toastLabel } = {}) {
  const queryClient = useQueryClient();
  const label = toastLabel ?? resource.replace(/-/g, " ");

  // The functions below call useQuery, so they must satisfy
  // react-hooks/rules-of-hooks naming (start with "use"). Callers receive
  // them aliased as `list` / `detail` via the return below — clean DX with
  // valid hook semantics.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const list = (params, queryOpts = {}) => useResourceList(resource, params, queryOpts);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const detail = (id, queryOpts = {}) => useResourceDetail(resource, id, queryOpts);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [resource] });

  const create = useMutation({
    mutationFn: (data) => adminApi.create(resource, data),
    onSuccess: () => {
      toast.success(`${capitalize(label)} created`);
      invalidate();
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? `Failed to create ${label}`),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => adminApi.update(resource, id, data),
    onSuccess: () => {
      toast.success(`${capitalize(label)} updated`);
      invalidate();
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? `Failed to update ${label}`),
  });

  const remove = useMutation({
    mutationFn: (id) => adminApi.remove(resource, id),
    onSuccess: () => {
      toast.success(`${capitalize(label)} deleted`);
      invalidate();
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? `Failed to delete ${label}`),
  });

  return { list, detail, create, update, remove, invalidate };
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* Renamed-with-"use" hooks so the rules-of-hooks linter can see them. */
function useResourceList(resource, params, queryOpts = {}) {
  return useQuery({
    queryKey: [resource, "list", params ?? null],
    queryFn: () => adminApi.list(resource, params),
    ...queryOpts,
  });
}

function useResourceDetail(resource, id, queryOpts = {}) {
  return useQuery({
    queryKey: [resource, "detail", id],
    queryFn: () => adminApi.get(resource, id),
    enabled: Boolean(id) && id !== "new",
    ...queryOpts,
  });
}
